import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { useCustomizationLocked } from "@/lib/use-customization-locked";
import { usePermissao } from "@/lib/use-permissao";
import { PriceTable } from "./PriceTable";
import { PriceTableManagePanel } from "./PriceTableManagePanel";
import { PriceScopeTabs, type PriceScopeTab } from "./PriceScopeTabs";
import { ClientPriceEditor } from "./ClientPriceEditor";
import { gruposDoCatalogo, mapaDePrecos, precoAjustado, rowKey, soOsDiferenciados, type CatalogItem } from "./precos-comum";

const cardCls = "rounded-xl border border-zinc-200 bg-white p-5 shadow-sm dark:border-border dark:bg-card";
const sectionTitleCls = "text-sm font-semibold text-zinc-900 dark:text-foreground";

export function PriceListSection() {
  const utils = trpc.useUtils();
  // Travado também pra quem só vê preço ("Ver preços" sem "Editar preços"): lê, não edita.
  const podeEditar = usePermissao("prices.manage");
  const locked = useCustomizationLocked() || !podeEditar;
  const [activeTab, setActiveTab] = useState<PriceScopeTab>("global");
  const [selectedTableId, setSelectedTableId] = useState<string | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [percentInput, setPercentInput] = useState("");
  const [showOnlyOverrides, setShowOnlyOverrides] = useState(false);

  const catalogQuery = trpc.prosthesis.catalog.myServiceCategories.useQuery();
  const partnersQuery = trpc.labPricing.clientPricing.listPartners.useQuery();
  const priceTablesQuery = trpc.labPricing.priceTables.list.useQuery();
  const priceListQuery = trpc.labPricing.priceList.list.useQuery();

  const priceTables = priceTablesQuery.data ?? [];
  const partners = partnersQuery.data ?? [];
  const individualPartners = partners.filter((p) => !p.priceTableId);

  // Auto-seleciona a primeira tabela/clínica disponível ao entrar numa aba sem nada selecionado
  // ainda (ex: acabou de trocar de aba, ou a tabela/clínica selecionada foi excluída/vinculada).
  useEffect(() => {
    if (activeTab === "tables" && !selectedTableId && priceTables.length > 0) {
      setSelectedTableId(priceTables[0].id);
    }
    if (activeTab === "clients" && !selectedClientId && individualPartners.length > 0) {
      setSelectedClientId(individualPartners[0].clientOrganizationId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, priceTables, individualPartners]);

  const tableId = activeTab === "tables" ? selectedTableId : null;
  const clientId = activeTab === "clients" ? selectedClientId : null;

  const tableOverridesQuery = trpc.labPricing.priceTables.listOverrides.useQuery({ priceTableId: tableId ?? "" }, { enabled: !!tableId });

  const invalidateTable = (priceTableId: string) => {
    utils.labPricing.priceList.list.invalidate();
    utils.labPricing.priceTables.list.invalidate();
    utils.labPricing.priceTables.listOverrides.invalidate({ priceTableId });
  };
  const upsertBase = trpc.labPricing.priceList.upsert.useMutation({
    onSuccess: () => {
      utils.labPricing.priceList.list.invalidate();
      toast.success("Preço atualizado.");
    },
    onError: (err) => toast.error(err.message || "Erro ao salvar preço."),
  });
  const renameTable = trpc.labPricing.priceTables.rename.useMutation({
    onSuccess: () => {
      if (tableId) invalidateTable(tableId);
      toast.success("Percentual atualizado.");
    },
    onError: (err) => toast.error(err.message || "Erro ao salvar percentual."),
  });
  const setTableOverride = trpc.labPricing.priceTables.setOverride.useMutation({
    onSuccess: () => {
      if (tableId) invalidateTable(tableId);
      toast.success("Preço personalizado salvo.");
    },
    onError: (err) => toast.error(err.message || "Erro ao salvar preço personalizado."),
  });
  const removeTableOverride = trpc.labPricing.priceTables.removeOverride.useMutation({
    onSuccess: () => {
      if (tableId) invalidateTable(tableId);
      toast.success("Preço restaurado pro padrão.");
    },
    onError: (err) => toast.error(err.message || "Erro ao restaurar preço."),
  });

  const baseMap = useMemo(() => mapaDePrecos(priceListQuery.data), [priceListQuery.data]);
  const tableOverrideMap = useMemo(() => mapaDePrecos(tableOverridesQuery.data), [tableOverridesQuery.data]);
  const selectedTable = tableId ? priceTables.find((t) => t.id === tableId) : undefined;
  const tablePercent = selectedTable?.adjustmentPercent ?? 0;

  const groups = useMemo(() => {
    const ativos = ((catalogQuery.data ?? []) as CatalogItem[]).filter((c) => c.active);
    return gruposDoCatalogo(ativos, (s, m) => {
      const base = baseMap.get(rowKey(s, m)) ?? null;
      if (tableId) return precoAjustado(base, tableOverrideMap.get(rowKey(s, m)), tablePercent);
      return { priceCents: base, badge: base != null ? "global" : null, hasOverride: false };
    });
  }, [catalogQuery.data, baseMap, tableOverrideMap, tableId, tablePercent]);
  const visibleGroups = showOnlyOverrides ? soOsDiferenciados(groups) : groups;

  const handleSave = (serviceCategoryId: string, materialOptionId: string | null, priceCents: number) => {
    if (tableId) setTableOverride.mutate({ priceTableId: tableId, serviceCategoryId, materialOptionId: materialOptionId ?? undefined, priceCents });
    else upsertBase.mutate({ serviceCategoryId, materialOptionId: materialOptionId ?? undefined, priceCents });
  };
  const handleRestore = (serviceCategoryId: string, materialOptionId: string | null) => {
    const row = (tableOverridesQuery.data ?? []).find((o) => rowKey(o.serviceCategoryId, o.materialOptionId) === rowKey(serviceCategoryId, materialOptionId));
    if (row) removeTableOverride.mutate({ id: row.id });
  };
  const handleApplyPercent = () => {
    const value = Number(percentInput.replace(",", "."));
    if (!Number.isFinite(value)) return toast.error("Percentual inválido.");
    if (tableId) renameTable.mutate({ id: tableId, adjustmentPercent: value });
  };
  const handleTabChange = (tab: PriceScopeTab) => {
    setActiveTab(tab);
    setPercentInput("");
    setShowOnlyOverrides(false);
  };

  return (
    // `id` como as outras três seções: o deep-link do hub usa a chave, e um dos quatro sem âncora
    // era só inconsistência esperando virar bug.
    <div id="precos" className={cardCls}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className={sectionTitleCls}>Preços</h2>
          <p className="mt-0.5 text-xs text-zinc-500 dark:text-muted-foreground">
            Preço-base do laboratório — por tabela nomeada ou por clínica parceira, percentual ou preço fixo próprios.
          </p>
        </div>
      </div>

      <div className="mt-3">
        <PriceScopeTabs
          activeTab={activeTab}
          onTabChange={handleTabChange}
          priceTables={priceTables}
          selectedTableId={selectedTableId}
          onSelectTable={(id) => {
            setSelectedTableId(id);
            setPercentInput("");
          }}
          individualPartners={individualPartners}
          selectedClientId={selectedClientId}
          onSelectClient={(id) => setSelectedClientId(id || null)}
          locked={locked}
        />
      </div>

      {/* A clínica com configuração individual tem o próprio editor (o mesmo da aba Preços do cadastro). */}
      {clientId ? (
        <ClientPriceEditor key={clientId} clientOrganizationId={clientId} locked={locked} />
      ) : (
        <>
          {selectedTable && <PriceTableManagePanel table={selectedTable} partners={partners} locked={locked} onDeleted={() => setSelectedTableId(null)} />}

          {tableId && (
            <>
              <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50/50 p-3 dark:border-border dark:bg-background/40">
                <span className="text-[12.5px] text-zinc-600 dark:text-muted-foreground">Aplicar desconto/acréscimo geral:</span>
                <input
                  value={percentInput}
                  onChange={(e) => setPercentInput(e.target.value)}
                  placeholder={String(tablePercent)}
                  className="h-8 w-20 rounded-md border border-zinc-200 bg-white px-2 text-right text-[13px] text-zinc-900 dark:border-border dark:bg-card dark:text-foreground"
                />
                <span className="text-[12.5px] text-zinc-500 dark:text-muted-foreground">%</span>
                {!locked && (
                  <button
                    type="button"
                    onClick={handleApplyPercent}
                    disabled={renameTable.isPending || !percentInput.trim()}
                    className="inline-flex h-8 items-center rounded-md bg-brand-600 px-3 text-[12.5px] font-semibold text-white shadow-sm transition-colors hover:bg-brand-700 disabled:opacity-50"
                  >
                    Aplicar
                  </button>
                )}
                <span className="text-[11px] text-zinc-400 dark:text-muted-foreground">Percentual atual: {tablePercent}%</span>
              </div>
              <label className="mt-3 flex w-fit items-center gap-1.5 text-xs font-medium text-zinc-600 dark:text-muted-foreground">
                <input
                  type="checkbox"
                  checked={showOnlyOverrides}
                  onChange={(e) => setShowOnlyOverrides(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-zinc-300 text-brand-600 focus:ring-brand-500 dark:border-border"
                />
                Ver apenas itens com preço fixo diferenciado nesta tabela
              </label>
            </>
          )}

          <div className="mt-4">
            {catalogQuery.isLoading || priceListQuery.isLoading ? (
              <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">Carregando...</p>
            ) : showOnlyOverrides && visibleGroups.length === 0 ? (
              <p className="py-6 text-center text-sm text-zinc-500 dark:text-muted-foreground">Nenhum item com preço fixo diferenciado nesta tabela ainda.</p>
            ) : (
              <PriceTable
                groups={visibleGroups}
                onSave={handleSave}
                onRestore={tableId && !locked ? handleRestore : undefined}
                saving={locked || upsertBase.isPending || setTableOverride.isPending || removeTableOverride.isPending}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
